import { stripMarkdownBold } from "commonComponents/utilities/MainNavigation/SearchDialog/entity/hitText";
import type { ISearchHit } from "commonComponents/utilities/MainNavigation/SearchDialog/search/search.types";

import { readAmount } from "../rows/rowFields";
import {
    type AgentAnswerPhase,
    type AgentProseSegment,
    emptyAnswer,
    type IAgentAnswer,
    type IAgentCitation,
    type IAgentHandleRecord,
    type IAgentToolCall,
} from "./agentAnswer.types";
import { stepLabel } from "./agentAnswerFields";
import { parseRecLinks } from "./agentText";

export type AgentHandleMap = Readonly<Record<string, IAgentHandleRecord>>;

/** What the `final` event contributes, once the server has finished the turn. */
export interface IAgentFinalInput {
    /** The authoritative answer text. NOT the concatenation of `text` events. */
    text: string;
    /**
     * Every handle the caller knows, not just this turn's — a chip in an
     * earlier turn still has to resolve.
     */
    handles: AgentHandleMap;
}

/**
 * Turns one agent turn into an {@link IAgentAnswer}, a step at a time.
 *
 * Pure reducers — no React, no network — so the envelope can be driven from a
 * captured stream. Fed from the server's stream events.
 */
export interface IAgentAnswerAdapter {
    begin(input: { turnId: string; question: string }): IAgentAnswer;
    /** From `tool_start`: the model asked, the tool has not run yet. */
    onToolCall(previous: IAgentAnswer, call: IAgentToolCall): IAgentAnswer;
    /** From `tool_end`: carries a duration and nothing about the result. */
    onToolEnd(previous: IAgentAnswer, input: { callId: string; ms: number | null }): IAgentAnswer;
    /** From `final`: prose and citations land together. */
    onFinal(previous: IAgentAnswer, input: IAgentFinalInput): IAgentAnswer;
    finish(
        previous: IAgentAnswer,
        outcome: { phase: AgentAnswerPhase; error: string | null },
    ): IAgentAnswer;
}

function handleToHit(record: IAgentHandleRecord): ISearchHit {
    return {
        entity: record.entity,
        id: record.id,
        score: 0,
        rerank_score: 0,
        reranker: "agent",
        summary: record.summary,
        payload: record.payload,
    };
}

/** Splits the answer on blank lines so each block renders as its own block. */
function toParagraphs(
    text: string,
    handles: AgentHandleMap,
): { paragraphs: AgentProseSegment[][]; citations: Record<string, IAgentCitation> } {
    const citations: Record<string, IAgentCitation> = {};
    const byHandle = new Map<string, string>();
    let nextOrdinal = 1;

    const blocks = text
        .split(/\n{2,}/)
        .map((block) => block.trim())
        .filter((block) => block.length > 0);

    // Segments render plain, so bold is stripped per block, before the rec-link
    // split would break a `**[Label](rec:r1)**` pair across segments.
    const paragraphs = blocks.map((block) =>
        parseRecLinks(stripMarkdownBold(block)).map<AgentProseSegment>((segment) => {
            if (segment.kind === "text") {
                return { kind: "text", text: segment.text.replace(/\*\*/g, "") };
            }

            const existing = byHandle.get(segment.handle);
            if (existing !== undefined) {
                return { kind: "citation", citationId: existing };
            }

            const id = String(nextOrdinal);
            nextOrdinal += 1;
            byHandle.set(segment.handle, id);
            const record = handles[segment.handle];
            citations[id] = {
                id,
                handle: segment.handle,
                label: segment.label,
                amount: record ? readAmount(handleToHit(record)) : null,
            };
            return { kind: "citation", citationId: id };
        }),
    );

    return { paragraphs, citations };
}

export const liveAgentAnswerAdapter: IAgentAnswerAdapter = {
    begin(input) {
        return emptyAnswer(input);
    },

    onToolCall(previous, call) {
        return {
            ...previous,
            phase: "thinking",
            // Append-only: a step that has been painted never moves or vanishes.
            steps: [
                ...previous.steps,
                {
                    id: call.id,
                    label: stepLabel(call),
                    status: "running",
                    elapsedMs: null,
                },
            ],
        };
    },

    onToolEnd(previous, input) {
        return {
            ...previous,
            steps: previous.steps.map((step) =>
                step.id === input.callId
                    ? { ...step, status: "complete" as const, elapsedMs: input.ms }
                    : step,
            ),
        };
    },

    onFinal(previous, input) {
        const { paragraphs, citations } = toParagraphs(input.text, input.handles);
        return {
            ...previous,
            phase: "answering",
            paragraphs,
            citations,
        };
    },

    finish(previous, outcome) {
        return {
            ...previous,
            phase: outcome.phase,
            error: outcome.error,
            // A turn that ended while a step was still running did not finish it.
            steps: previous.steps.map((step) =>
                step.status === "running"
                    ? { ...step, status: outcome.error === null ? "complete" : "error" }
                    : step,
            ),
        };
    },
};
